import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from './ConfirmationModal';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  PushPin as PinIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  Attachment as AttachmentIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import PostComments from './PostComments';

interface ClassPost {
  id: number;
  class_id: number;
  class_name: string;
  author_id: number;
  author_name: string;
  post_type: string;
  title: string | null;
  content: string;
  is_pinned: boolean;
  comments_count: number;
  attachments_count: number;
  attachments: any[];
  created_at: string;
  updated_at: string;
}

interface ClassPostCardProps {
  post: ClassPost;
  onDelete?: (postId: number) => void;
  onTogglePin?: (postId: number) => void;
  onUpdate?: (postId: number, updates: any) => Promise<void>;
}

const ClassPostCard: React.FC<ClassPostCardProps> = ({ post, onDelete, onTogglePin, onUpdate }) => {
  const isTeacher = !!(onDelete && onTogglePin && onUpdate);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedContent, setEditedContent] = useState(post.content);
  const [editedTitle, setEditedTitle] = useState(post.title || '');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePin = () => {
    if (onTogglePin) onTogglePin(post.id);
    handleMenuClose();
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (onDelete) onDelete(post.id);
    setDeleteConfirmOpen(false);
  };

  const handleSaveEdit = async () => {
    try {
      if (onUpdate) {
        await onUpdate(post.id, {
          content: editedContent,
          title: editedTitle || null
        });
      }
      setEditDialogOpen(false);
    } catch (err) {
      alert('Failed to update post');
    }
  };

  const getPostTypeColor = (type: string) => {
    switch (type) {
      case 'announcement':
        return 'primary';
      case 'material':
        return 'secondary';
      case 'assignment_link':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPostTypeIcon = (type: string) => {
    switch (type) {
      case 'announcement':
        return '📢';
      case 'material':
        return '📚';
      case 'assignment_link':
        return '📝';
      default:
        return '📄';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      const now = new Date();
      const date = new Date(dateString);
      const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
      
      if (seconds < 60) return 'just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
      const weeks = Math.floor(days / 7);
      if (weeks < 4) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
      const years = Math.floor(days / 365);
      return `${years} year${years !== 1 ? 's' : ''} ago`;
    } catch {
      return new Date(dateString).toLocaleDateString();
    }
  };

  return (
    <>
      <Card className={`class-post-card ${post.is_pinned ? 'pinned' : ''}`} sx={{ mb: 2 }}>
        <CardContent>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" gap={2} flex={1}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {post.author_name.charAt(0)}
              </Avatar>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {post.author_name}
                  </Typography>
                  <Chip
                    label={`${getPostTypeIcon(post.post_type)} ${t(`classFeed.postTypes.${post.post_type}`)}`}
                    size="small"
                    color={getPostTypeColor(post.post_type) as any}
                  />
                  {post.is_pinned && (
                    <Chip
                      icon={<PinIcon />}
                      label={t('classFeed.pinned')}
                      size="small"
                      color="warning"
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatTimeAgo(post.created_at)}
                  {post.updated_at !== post.created_at && ' (edited)'}
                </Typography>
              </Box>
            </Box>
          {isTeacher && (
            <IconButton 
              onClick={handleMenuOpen} 
              size="small"
              aria-label="Post options"
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl)}
            >
              <MoreVertIcon />
            </IconButton>
          )}
          </Box>

          {/* Content */}
          {post.title && (
            <Typography variant="h6" gutterBottom>
              {post.title}
            </Typography>
          )}
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
            {post.content}
          </Typography>

          {/* Attachments */}
          {post.attachments_count > 0 && (
            <Box mt={2}>
              {post.attachments.map((attachment: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    mb: 1
                  }}
                >
                  {attachment.attachment_type === 'link' ? (
                    <LinkIcon fontSize="small" />
                  ) : (
                    <AttachmentIcon fontSize="small" />
                  )}
                  <Typography
                    variant="body2"
                    component="a"
                    href={attachment.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {attachment.attachment_name || attachment.attachment_url}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>

        {/* Actions */}
        <CardActions sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <CommentIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {post.comments_count} {post.comments_count === 1 ? t('classFeed.comment') : t('classFeed.comments')}
            </Typography>
          </Box>
        </CardActions>

        {/* Comments Section */}
        <Box sx={{ px: 2, pb: 2 }}>
          <PostComments 
            postId={post.id} 
            commentsCount={post.comments_count}
            onCommentAdded={() => {
              // Optionally refresh to update comment count
            }}
          />
        </Box>

        {/* Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handlePin}>
            <PinIcon fontSize="small" sx={{ mr: 1 }} />
            {post.is_pinned ? t('classFeed.unpin') : t('classFeed.pin')}
          </MenuItem>
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.edit')}
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.delete')}
          </MenuItem>
        </Menu>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('classFeed.editPost')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('classFeed.title')}
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            fullWidth
            margin="normal"
            placeholder={t('classFeed.titlePlaceholder')}
          />
          <TextField
            label={t('classFeed.content')}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            fullWidth
            multiline
            rows={6}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={!editedContent.trim()}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        title={t('classFeed.deletePostTitle', 'Delete Post')}
        message={t('classFeed.confirmDeletePost')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};

export default ClassPostCard;

